import { useState } from "react";
import { Flag, ShieldOff, MoreVertical, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const REPORT_REASONS = [
  "Fake profile / catfishing",
  "Harassment or abuse",
  "Inappropriate photos",
  "Spam or scam",
  "Underage user",
  "Other",
];

/**
 * A three-dots overflow menu shared across Discover / Chat / Matches.
 * Handles both reporting and blocking a user.
 *   - targetId (required): user id to act against
 *   - targetName (required): displayed in modal copy
 *   - context: 'profile' | 'chat' | 'fixture'
 *   - onBlocked: callback after successful block (e.g. remove card, close chat)
 *   - variant: 'card' | 'header' — controls button styling
 */
export default function SafetyMenu({ targetId, targetName, context = "profile", onBlocked, variant = "card" }) {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null); // null | 'report' | 'block'
  const [reason, setReason] = useState(REPORT_REASONS[0]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submitReport = async () => {
    setBusy(true);
    try {
      const fullReason = note.trim() ? `${reason} — ${note.trim()}` : reason;
      await api.post("/report", { target_id: targetId, reason: fullReason, context });
      toast.success("Reported. Our team will review.");
      setModal(null);
      setOpen(false);
      setNote("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Report failed");
    } finally { setBusy(false); }
  };

  const submitBlock = async () => {
    setBusy(true);
    try {
      await api.post("/block", { target_id: targetId });
      toast.success(`${targetName || "User"} blocked.`);
      setModal(null);
      setOpen(false);
      onBlocked?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Block failed");
    } finally { setBusy(false); }
  };

  const triggerClass = variant === "header"
    ? "p-2 rounded-full hover:bg-white/10 text-slate-200"
    : "w-9 h-9 rounded-full bg-black/40 backdrop-blur border border-white/20 flex items-center justify-center hover:bg-black/60";

  return (
    <>
      <button type="button" onClick={(e)=>{ e.stopPropagation(); setOpen(true); }}
        className={triggerClass} title="Safety options" data-testid="safety-menu-trigger">
        <MoreVertical size={variant === "header" ? 20 : 18} />
      </button>

      {open && !modal && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-6"
             onClick={()=>setOpen(false)} data-testid="safety-menu-sheet">
          <div className="w-full max-w-sm bg-footy-card border border-white/10 sm:rounded-2xl rounded-t-2xl p-2"
               onClick={(e)=>e.stopPropagation()}>
            <button onClick={()=>setModal("report")}
              className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-white/5 text-left"
              data-testid="safety-report">
              <Flag size={18} className="text-footy-red" />
              <div>
                <div className="font-semibold">Report {targetName || "user"}</div>
                <div className="text-xs text-slate-400">Our team will review within 24h</div>
              </div>
            </button>
            <button onClick={()=>setModal("block")}
              className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-white/5 text-left"
              data-testid="safety-block">
              <ShieldOff size={18} className="text-footy-red" />
              <div>
                <div className="font-semibold">Block {targetName || "user"}</div>
                <div className="text-xs text-slate-400">You won't see or hear from each other again</div>
              </div>
            </button>
            <button onClick={()=>setOpen(false)}
              className="w-full flex items-center justify-center gap-2 p-3 text-slate-400 text-sm mt-1 border-t border-white/10"
              data-testid="safety-cancel">
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {modal === "report" && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
             onClick={()=>setModal(null)} data-testid="safety-report-modal">
          <div className="w-full max-w-sm bg-footy-card border border-white/10 rounded-2xl p-6"
               onClick={(e)=>e.stopPropagation()}>
            <p className="text-xs uppercase tracking-[0.25em] text-footy-red font-bold">Safety</p>
            <h2 className="font-display text-3xl uppercase mt-2 leading-none">Report {targetName || "user"}</h2>
            <p className="text-sm text-slate-400 mt-3">Reports are anonymous. We review every one — thank you for keeping Footymatch a safe terrace.</p>
            <label className="footy-label mt-6">Reason</label>
            <div className="space-y-2">
              {REPORT_REASONS.map(r => (
                <button key={r} type="button" onClick={()=>setReason(r)}
                  className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm ${reason === r ? "border-footy-red bg-footy-red/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
                  data-testid={`report-reason-${r.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
                  {r}
                </button>
              ))}
            </div>
            <label className="footy-label mt-4">Anything else? (optional)</label>
            <textarea value={note} onChange={(e)=>setNote(e.target.value)} rows={3} maxLength={500}
              placeholder="Give us any detail that helps us investigate."
              className="footy-input resize-none" data-testid="report-note" />
            <div className="mt-6 flex gap-3">
              <button onClick={()=>setModal(null)} className="footy-btn-secondary flex-1" data-testid="report-cancel">Back</button>
              <button onClick={submitReport} disabled={busy} className="footy-btn-primary flex-1" data-testid="report-submit">
                {busy ? "Sending..." : "Send report"}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === "block" && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
             onClick={()=>setModal(null)} data-testid="safety-block-modal">
          <div className="w-full max-w-sm bg-footy-card border border-white/10 rounded-2xl p-6"
               onClick={(e)=>e.stopPropagation()}>
            <p className="text-xs uppercase tracking-[0.25em] text-footy-red font-bold">Safety</p>
            <h2 className="font-display text-3xl uppercase mt-2 leading-none">Block {targetName || "user"}?</h2>
            <p className="text-sm text-slate-400 mt-3">You won't see each other in Discover, matches, or chat. This can be undone in your Profile settings.</p>
            <div className="mt-6 flex gap-3">
              <button onClick={()=>setModal(null)} className="footy-btn-secondary flex-1" data-testid="block-cancel">Cancel</button>
              <button onClick={submitBlock} disabled={busy} className="footy-btn-primary flex-1" data-testid="block-submit">
                {busy ? "Blocking..." : "Block"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
